import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BarChart3, Map, Package, ExternalLink } from 'lucide-react';
import STATIC_BASE_URL from '../staticConfig';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const projectsData = {
    panel: {
      title: 'Panel de Pedidos',
      description:
        "El panel de pedidos es una herramienta visual e interactiva que permite a los usuarios monitorear en tiempo real el progreso de sus pedidos.",
      icon: BarChart3,
      backgroundImage: "/images/PANEL.png", // 
      features: [
        'Visualizar en tiempo real la información del estatus de cada pedido.',
        'Mejorar el flujo de la información sin depender de intermediarios.',
        'Navegación clara e intuitiva.',
        'Disponer de un registro histórico que permita dar seguimiento y análisis a pedidos entregados.',
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'Socket.io', 'Chart.js'],
  path: `${STATIC_BASE_URL}/sincronizar_excel/sincronizar_excel/mostrar_datos.php`,
    },
    mapeo: {
      title: 'Sistema de Mapeo',
      description:
        'Permite visualizar de manera interactiva las ubicaciones estratégicas de plantas, puntos de origen, proveedores y prospectos...',
      icon: Map,
      backgroundImage: "/images/MAPEO.png", 
      features: [
        'Reducir costos de distribución mediante la planificación estratégica de entregas.',
        'Eficientar el rendimiento de las rutras de distribución logística.',
        'Localizar de forma interactiva las plantas de clientes, provedores y prospectos.',
        'Analizar cobertura geográfica para evaluar la presencia en el mercado.',
      ],
      technologies: ['React', 'Mapbox', 'PostGIS', 'Python', 'Three.js'],
  path: `${STATIC_BASE_URL}/VISION_E5/conexionphp/LEAFLETP/index.php`,
    },
    cubo: {
      title: 'Cubo Comercial',
      description:
        'Es una herramienta interactiva que permite analizar y explorar datos de ventas a través de una interfaz...',
      icon: Package,
      backgroundImage: "/images/CUBO.png", // 
      features: [
        'Monitorear el rendimiento en tiempo real mediante KPIs clave del negocio.',
        'Interpretar el comportamiento del mercado y anticipar tendencias.',
        'Tomar decisiones informadas basadas en datos',
        'Anticipar resultados y planificar estrategias comerciales.',
        'Detectar áreas de oportunidad y riesgo.',
        'Evaluar el cumplimiento de la meta de ventas y objetivos',
      ],
      technologies: ['React', 'Python', 'TensorFlow', 'Apache Spark', 'D3.js'],
  path: `${STATIC_BASE_URL}/E5-CUBO-COMERCIAL/index.php`,
    },
  };

  const project = projectsData[projectId];

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Proyecto no encontrado
          </h1>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const Icon = project.icon;

  return (
    <div className="min-h-screen bg-white">
      <motion.div
        className="relative text-white py-16 px-6 bg-cover bg-center"
        style={{ backgroundImage: `url(${project.backgroundImage})` }} // 👈 Fondo dinámico
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Overlay opcional para mejorar contraste */}
        <div className="absolute inset-0 bg-black/40"></div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 mb-8 px-4 py-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors"
            whileHover={{ x: -5 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Volver al inicio
          </motion.button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-white/20 rounded-2xl"></div>
                <div>
                  <h1 className="text-4xl font-bold">{project.title}</h1>
                  <p className="text-white/80 text-lg">
                    {project.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <motion.button
                  className="flex items-center gap-2 px-6 py-3 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => (window.location.href = project.path)}
                >
                  <ExternalLink className="w-5 h-5" />
                  Acceder al sistema
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Características Principales
            </h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {project.features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 + index * 0.1 }}
                >
                <div className="p-4 bg-white/20 rounded-2xl">
                  <img
                    src="/images/matrazn.png"
                    alt="matraz rojo"
                    className="w-12 h-12"
                  />
                </div>
                  <p className="text-gray-700 font-medium leading-relaxed">
                    {feature}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
